import * as FRYWorkflows from '@fry/booking/lib/workflow';
import * as FRYUserFields from '@fry/lib/user-fields';
import * as FRYEASForm from '@fry/components/common/easform';

import * as TypeGuard from './type-guards';

import { Workflow } from './workflow.model';
import { Transition } from './transition.model';
import { TransitionGuard, TransitionGuardData } from './transition-guard.model';
import { State } from './state.model';
import { TransitionAction, TransitionActionData } from './transition-action.model';
import { Form } from './form.model';
import { FormPermission } from './form-permission.model';
import { FormField, FormFieldVisibility } from './form-field.model';
import {
  FormFieldType,
  FormFieldTypeGeneral,
  FormFieldTypeUserField
} from './form-field-type.model';
import { BackendObjectRef } from './backend-integration.service';

export class BackendIntegrationSerializer {

  // Backend requires certain structure to be passed to it even if the Form
  // is not defined for the workflow.
  public static EMPTY_WORKFLOW_FORM(): FRYWorkflows.WorkflowDocFormGroupSchema {
    return { fields: [], type: 'group', id: 'root' };
  }

  static deserializeWorkflow(workflow: FRYWorkflows.Workflow,
                             roles: BackendObjectRef[],
                             availableActions: TransitionAction[],
                             availableGuards: TransitionGuard[],
                             availableGeneralFields: FormFieldTypeGeneral[],
                             availableUserFields: FormFieldTypeUserField[]): Workflow {
    // States
    const states = workflow.doc.form.states.map((state: FRYWorkflows.StateSchema) => {
      return BackendIntegrationSerializer.deserializeState(state, roles);
    });

    // Transitions
    const transitions: Transition[] = workflow.doc.form.transitions.map(data => {
      const transition =
        BackendIntegrationSerializer.deserializeTransition(data,
                                                           states,
                                                           availableActions,
                                                           availableGuards);
      return transition;
    });

    // Transition permissions
    workflow.doc.form.states.forEach((state: FRYWorkflows.StateSchema) => {
      state.roles.forEach((permission: FRYWorkflows.StatePermissionSchema) => {
        if (!permission.transitions) { return; }
        permission.transitions.forEach((transitionID: string) => {
          const transition = transitions.find(t => t.id === transitionID);
          const role = roles.find(r => r.id === permission.role);
          // FIXME: What to do in this case?
          if (!transition || !role) { return; }
          transition.roles.push(role);
        });
      });
    });

    // Initial transition
    const initial =
        BackendIntegrationSerializer.deserializeInitialTransition(workflow.doc.form.initial,
                                                                  states,
                                                                  roles,
                                                                  availableActions);

    // Workflow form fields
    const fields: FormField[] = workflow.doc.form.group.fields.map(payload => {
      const field =
        BackendIntegrationSerializer.deserializeFormField(payload,
                                                          availableUserFields,
                                                          availableGeneralFields,
                                                          roles);
      return field;
    });

    // Workflow form permissions
    const formPermissions: FormPermission[] = [];
    workflow.doc.form.states.forEach((state: FRYWorkflows.StateSchema) => {
      state.roles.forEach((permission: FRYWorkflows.StatePermissionSchema) => {
        if (!permission.group) { return; }
        Object.keys(permission.group).forEach(fieldID => {
          const role = roles.find(r => r.id === permission.role);
          if (!role) { return; }
          const visible = true;
          const editable = permission.group[fieldID] === 'edit';
          const perm = new FormPermission(fieldID, role.id, state.id, visible, editable);
          formPermissions.push(perm);
        });
      });
    });

    // Final Workflow form
    const form = new Form(workflow.doc.form.id,
                          workflow.doc.form.name,
                          fields,
                          formPermissions);

    // Final Workflow
    const result =
      new Workflow(workflow.id,
                   workflow.doc.name,
                   states,
                   transitions,
                   initial,
                   form);
    return result;
  }

  static deserializeState(data: FRYWorkflows.StateSchema, availableRoles: BackendObjectRef[]): State {
    // Deserialize `canDelete` permission from the state permission
    const roles = (data.roles as FRYWorkflows.StatePermissionSchema[])
                    .filter(permission  => permission.canDelete === true)
                    .map(permission => permission.role)
                    .reduce((ids, id) => {
                      if (!ids.includes(id)) { ids.push(id); }
                      return ids;
                    }, [] as string[])
                    .map(id => availableRoles.find(r => r.id === id))
                    .filter(TypeGuard.isDefined);

    // Deserialize State
    return new State(data.id, data.name, false, roles);
  }

  static deserializeTransition(data: FRYWorkflows.TransitionSchema,
                               states: State[],
                               availableActions: TransitionAction[],
                               availableGuards: TransitionGuard[]): Transition {
      const source = states.find(s => data.sources.find(ts => ts === s.id));
      const target = states.find(s => s.id === data.destination);
      if (!source || !target) {
        throw new Error($localize `Unable to deserialize transition states!`);
      }

      const confirmation = {
        cancelButtonTitle: data.cancelButton,
        continueButtonTitle: data.okButton,
        description: data.confirmationDescription,
        requireConfirmation: data.requireConfirmation,
        title: data.confirmationTitle,
      };

      const actions = (data.actions ?? [])
                      .map(action => BackendIntegrationSerializer.deserializeTransitionAction(action, availableActions));
      const guards = (data.guards ?? [])
                     .map(guard => BackendIntegrationSerializer.deserializeTransitionGuard(guard, availableGuards));

      const transition = new Transition(data.id,
                                        [],
                                        data.name as string,
                                        source,
                                        target,
                                        actions,
                                        data.autoTransition,
                                        data.timeTriggeredTransition,
                                        (data.timeExpiration || 0) / 60,
                                        confirmation,
                                        guards,
                                        data.message,
                                        !data.allowInvalidForm);
      return transition;
  }

  static deserializeInitialTransition(data: FRYWorkflows.InitialTransitionSchema,
                                      states: State[],
                                      roles: BackendObjectRef[],
                                      availableActions: TransitionAction[]): Transition|undefined {
      // If we don't get the data or if it's the 'no-value payload' we return
      // undefined (this is ideal for Result error handling)
      if (!data) { return undefined; }
      if (data.state === null && data.actions.length === 0) { return undefined; }

      const target = states.find(s => s.id === data.state);
      if (!target) {
        throw new Error(`Unable to deserialize transition states!`);
      }

      const actions = data.actions.map(action => BackendIntegrationSerializer.deserializeTransitionAction(action, availableActions));

      const payloadRoles = data.roles ?? [];
      const transitionRoles = roles.filter(r => payloadRoles.find(dr => r.id === dr));

      const transition = Transition.initialTransition(target, transitionRoles, actions);
      return transition;
  }

  static deserializeTransitionGuard(value: FRYWorkflows.TransitionGuardSchema,
    templates: TransitionGuard[]): TransitionGuard {
    const template = templates.find(guard => guard.type === value.guard);
    if (!template) {
      throw new Error($localize `Unable to find '${value.guard} guard template!`);
    }
    const data = value.data ? value.data as TransitionGuardData : undefined;
    return template.evalueToGuard(undefined, data);
  }

  static deserializeTransitionAction(value: FRYWorkflows.TransitionActionSchema,
                                     templates: TransitionAction[]): TransitionAction {
    const template = templates.find(action => action.type === value.action);
    if (!template) {
      throw new Error($localize `Unable to find '${value.action} action template!`);
    }
    const data = value.data ? value.data as TransitionActionData : {};
    return template.evaluateToAction(data);
  }

  static deserializeBackendObjectRef(value: any): BackendObjectRef {
    return { id: value.doc._id, name: value.doc.title ?? value.doc.name };
  }

  static deserializeFormField(payload: FRYWorkflows.WorkflowDocFormFieldSchema,
                              availableUserFields: FormFieldTypeUserField[],
                              availableGeneralFields: FormFieldTypeGeneral[],
                              roles: BackendObjectRef[]): FormField {
    const fieldType =
      BackendIntegrationSerializer.deserializeFormFieldType(payload,
                                                            availableUserFields,
                                                            availableGeneralFields);

    let visibility: FormFieldVisibility|undefined;
    if (payload.options.meta && payload.options.meta.visible) {
      visibility = {
        operator: payload.options.meta.visible.op,
        target: payload.options.meta.visible.value,
        value: payload.options.meta.visible.cmp,
        visibility: 'visible',
      };
    } else if (payload.options.meta && payload.options.meta.hidden) {
      visibility = {
        operator: payload.options.meta.hidden.op,
        target: payload.options.meta.hidden.value,
        value: payload.options.meta.hidden.cmp,
        visibility: 'hidden',
      };
    }

    const values: BackendObjectRef[]|undefined = payload.options.categories
                   ? payload.options.categories.map(c => {
                      return { id: c._id, name: c.name };
                     })
                   : undefined;

    const sourceFilter = payload.options.sourceFilter || {};
    const wfRoles: string[] | undefined = sourceFilter.roles;
    const usedRoles: BackendObjectRef[]|undefined = wfRoles ?
      wfRoles.map(
        (roleID: string) => {
          const role = roles.find(r => r.id === roleID);
          return role;
        }
      ).filter(role => role !== undefined) as BackendObjectRef[] : undefined;

    const content: string|undefined = payload.options.content ?? undefined;

    return new FormField(payload.id,
                         payload.options.label ?? '',
                         fieldType,
                         payload.options.required,
                         visibility,
                         values,
                         usedRoles,
                         content,
                         payload.asyncValidators,
                         payload.validators);
  }

  static deserializeFormFieldType(field: FRYWorkflows.WorkflowDocFormFieldSchema,
                                  availableUserFields: FormFieldTypeUserField[],
                                  availableGeneralFields: FormFieldTypeGeneral[]): FormFieldType {
    const isUserField = field.fieldType === FRYUserFields.UserFieldsStore.DOC_TYPE;
    let fieldType: FormFieldType|undefined;
    fieldType = isUserField
                ? availableUserFields.find(t => t.id === field.options.related)
                : availableGeneralFields.find(t => t.id === field.fieldType);
    if (!fieldType) {
      const typeLabel = isUserField
                        ? `User field: ${field.options.related}`
                        : field.fieldType
      const name = `Unknown/Invalid field type - '${typeLabel}'`
      fieldType = isUserField
                  ? new FormFieldTypeUserField(field.id, name, undefined)
                  : new FormFieldTypeGeneral(field.fieldType, name);
    }

    return fieldType;
  }

  static deserializeUserFieldType(value: FRYUserFields.UserField): FormFieldTypeUserField {
    type Category = {_id: string, name: string};
    const categories: Category[]|undefined = value.doc['categories'];
    const values: BackendObjectRef[]
      = categories !== undefined
        ? categories.map((c: Category) => ({ id: c._id, name: c.name }))
        : [];
    return new FormFieldTypeUserField(value.id,
                                      value.title,
                                      value.fieldType,
                                      values)
  }

  static deserializeGeneralFieldType(value: BackendObjectRef): FormFieldTypeGeneral {
    return new FormFieldTypeGeneral(value.id, value.name);
  }


  //
  // Serialization
  //

  static serializeWorkflow(workflow: Workflow): FRYWorkflows.WorkflowDocFormSchema {
    const transitionsPayload = workflow.transitions.map(transition => {
      return BackendIntegrationSerializer.serializeTransition(transition);
    });

    const initialTransitionPayload =
      BackendIntegrationSerializer.serializeInitialTransition(workflow.initialTransition);

    const statesPayload = workflow.states.map(state => {
      const trans = workflow.transitionsForState(state);
      const permissions =
        BackendIntegrationSerializer.serializePermissionsForState(state, trans, workflow.form?.permissions ?? []);
      return BackendIntegrationSerializer.serializeState(state, permissions);
    });

    const result: FRYWorkflows.WorkflowDocFormSchema = {
      id: workflow.id, // We can provide this but it gets overriden by backend
      name: workflow.name,
      states: statesPayload,
      transitions: transitionsPayload,
      initial: initialTransitionPayload,
      group: BackendIntegrationSerializer.serializeWorkflowForm(workflow.form)
    };
    return result;
  }

  static serializeWorkflowForm(form: Form|undefined): FRYWorkflows.WorkflowDocFormGroupSchema {
    const result = BackendIntegrationSerializer.EMPTY_WORKFLOW_FORM();
    if (!form) { return result; }

    // Serialize form fields
    result.fields = form.fields.map(field => {
      return BackendIntegrationSerializer.serializeFormField(field);
    });

    return result;
  }

  static serializeFormField(field: FormField): FRYWorkflows.WorkflowDocFormFieldSchema {
    // Serialize the Form Field
    const result: FRYWorkflows.WorkflowDocFormFieldSchema = {
      fieldType: field.type.id,
      id: field.id,
      options: { required: field.required, label: field.name },
      type: FRYEASForm.EasFieldType.Field,
      asyncValidators: field.asyncValidators,
      validators: field.syncValidators
    };

    // Adjust field type for the User Field
    if (field.type.isUserField) {
      result.fieldType = FRYUserFields.UserFieldsStore.DOC_TYPE;
      result.options.related = field.type.id;
    }

    // Serialize field metadata
    result.options.meta =
      BackendIntegrationSerializer.serializeFormFieldMetadata(field);

    // Serialize discrete field type options
    if (field.type.isDiscrete && field.availableValues) {
      result.options.categories = field.availableValues.map(value => {
        const category: FRYWorkflows.WorkflowDocFormFieldOptionsCategorySchema = {
          _id: value.id, name: value.name
        };
        return category;
      });
    } else {
      result.options.categories = undefined;
    }

    if (field.type.isAssignee) {
      result.options.sourceFilter = {
        roles: field.roles?.map(role => role.id) ?? []
      };
    }

    // Serialize content
    if (field.type.isContentRequired && field.type.isContentRequired) {
      result.options.content = field.content;
    } else {
      result.options.content = undefined;
    }

    return result;
  }

  static serializeFormFieldVisibility(visibility: FormFieldVisibility): FRYWorkflows.WorkflowDocFormFieldOptionsVisibilitySchema {
    return { cmp: visibility.value, op: visibility.operator, value: visibility.target };
  }

  static serializeFormFieldMetadata(field: FormField): Record<string,any> {
    const result: Record<string,any> = {};
    if (field.visibility) {
      result[field.visibility.visibility] =
        BackendIntegrationSerializer.serializeFormFieldVisibility(field.visibility);
    }
    return result;
  }

  static serializePermissionsForState(state: State,
                                      transitions: Transition[],
                                      formPermissions: FormPermission[]): FRYWorkflows.StatePermissionSchema[] {
    // Transition permisssios
    const permissions: FRYWorkflows.StatePermissionSchema[] =
      transitions
      // Filter only transitions which are for state in question
      .filter(transition => transition.source.id === state.id)
      // For each of the roles of the transition create the payload
      .reduce((acc: FRYWorkflows.StatePermissionSchema[], transition: Transition) => {
        const perms = transition.roles.map(role => {
          return { canDelete: false, group: {}, role: role.id, transitions: [transition.id] };
        });
        return acc.concat(perms);
      }, [])
      // Reduce duplicate role payloads
      .reduce((acc: FRYWorkflows.StatePermissionSchema[], current: FRYWorkflows.StatePermissionSchema) => {
        const idx = acc.findIndex(p => p.role === current.role);
        if (idx === -1) {
          acc.push(current);
        } else {
          acc[idx].transitions?.push(...(current.transitions ?? []))
        }
        return acc;
      }, []);

    // FormField permissions
    formPermissions.filter(fp => fp.state === state.id)
                   .forEach(fp => {
                     const index = permissions.findIndex(p => p.role === fp.role);
                     if (index === -1) {
                        const permission: FRYWorkflows.StatePermissionSchema
                          = { canDelete: false, group: {}, role: fp.role, transitions: [] };
                        permission.group[fp.object] = fp.editable ? 'edit' : 'view';
                        permissions.push(permission);
                     } else {
                        permissions[index].group[fp.object] = fp.editable ? 'edit' : 'view';
                     }
                   });

    // Can delete booking permission
    state.canDeleteRoles.forEach(role => {
      const perms = permissions.filter(permission => permission.role === role.id);
      if (perms.length === 0) {
        permissions.push({ canDelete: true, group: {}, role: role.id, transitions: [] });
        return;
      }

      perms.forEach(permission => {
        const index = permissions.indexOf(permission);
        permissions[index].canDelete = true;
      });
    });

    return permissions;
  }

  static serializeState(state: State, permissions: FRYWorkflows.StatePermissionSchema[]): FRYWorkflows.StateSchema {
    const result: FRYWorkflows.StateSchema = {
      id: state.id, name: state.name, roles: permissions
    };
    return result;
  }

  static serializeTransition(transition: Transition): FRYWorkflows.TransitionSchema {
    const result: FRYWorkflows.TransitionSchema = {
      actions: transition.actions.map(action => BackendIntegrationSerializer.serializeTransitionAction(action)),
      allowInvalidForm: !transition.requireValidForm,
      autoTransition: transition.autoTransition,
      timeTriggeredTransition: transition.timeTriggeredTransition,
      timeExpiration: (transition.timeExpiration || 0) * 60,
      destination: transition.target.id,
      guards: transition.guards.map(guard => BackendIntegrationSerializer.serializeTransitionGuard(guard)),
      id: transition.id,
      message: transition.message,
      name: transition.name,
      sources: [transition.source.id],
      requireConfirmation: transition.confirmation.requireConfirmation,
    };

    if (result.requireConfirmation) {
      result.okButton                = transition.confirmation.continueButtonTitle;
      result.confirmationTitle       = transition.confirmation.title;
      result.confirmationDescription = transition.confirmation.description;
      result.cancelButton            = transition.confirmation.cancelButtonTitle;
    }

    return result;
  }

  static serializeInitialTransition(transition: Transition|undefined): FRYWorkflows.InitialTransitionSchema {
    // If we don't have initial transition we still need to provide empty payload
    if (!transition) {
      return {actions: [], roles: [], state: ''};
    }

    const result: FRYWorkflows.InitialTransitionSchema = {
      actions: transition.actions.map(action => BackendIntegrationSerializer.serializeTransitionAction(action)),
      roles: transition.roles.map(r => r.id),
      state: transition.target.id,
    };

    return result;
  }

  static serializeTransitionGuard(value: TransitionGuard): FRYWorkflows.TransitionGuardSchema {
    return { guard: value.type, data: value.data };
  }

  static serializeTransitionAction(value: TransitionAction): FRYWorkflows.TransitionActionSchema {
    return { action: value.type, data: value.data };
  }

}
